import type { EChartsOption } from "echarts";
import { S25Assert } from "../../util/s25.assert";
import {
    ReportBreakdown,
    ReportBreakdownRow,
    ReportChartType,
    ReportSegment,
    ReportSeries,
    ReportValueFormatter,
} from "./report.types";

/** Categorical chart palette (Tableau 10). Chart series colors, not UI chrome, so no design token applies. */
export const REPORT_PALETTE = [
    "#4e79a7",
    "#f28e2b",
    "#e15759",
    "#76b7b2",
    "#59a14f",
    "#edc948",
    "#b07aa1",
    "#ff9da7",
    "#9c755f",
    "#bab0ac",
] as const;

/** Base + lighter tint for zebra-striped single-series bars. */
export const REPORT_BAR_COLOR_PAIR = { primary: REPORT_PALETTE[0], alternate: "#7ba3c4" } as const;

export const REPORT_NO_DATA_TEXT = "No data";

export const formatReportNumber: ReportValueFormatter = (value) => value.toLocaleString();

/** Data point shape; `rowKey` / `seriesKey` ride along so chart clicks can drill down. */
interface ReportDataPoint {
    value: number;
    name?: string;
    rowKey: string;
    seriesKey: string;
}

export interface ReportChartOptionParams {
    breakdown: ReportBreakdown | undefined;
    series: ReportSeries[];
    measure: string;
    chartType: ReportChartType;
    formatValue?: ReportValueFormatter;
}

export function noDataOption(text: string = REPORT_NO_DATA_TEXT): EChartsOption {
    return { title: { text, left: "center", top: "middle" } };
}

function barColorForIndex(dataIndex: number): string {
    return dataIndex % 2 === 0 ? REPORT_BAR_COLOR_PAIR.primary : REPORT_BAR_COLOR_PAIR.alternate;
}

function measureOf(row: ReportBreakdownRow, measure: string): number {
    return row.measures?.[measure] ?? 0;
}

function toDataPoint(row: ReportBreakdownRow, measure: string): ReportDataPoint {
    return { value: measureOf(row, measure), name: row.label, rowKey: row.key, seriesKey: row.seriesKey };
}

/** Widgets with more than one series (e.g. fiscal-year comparison) always render as grouped bars. */
export function resolveReportChartType(chartType: ReportChartType, seriesCount: number): ReportChartType {
    if (seriesCount > 1 && chartType !== "cards") {
        return "groupedBar";
    }
    return chartType;
}

/** Horizontal bars ranked by value, largest at the top. */
export function rankedBarOption(
    rows: ReportBreakdownRow[],
    measure: string,
    formatValue: ReportValueFormatter = formatReportNumber,
): EChartsOption {
    if (!rows.length) {
        return noDataOption();
    }
    const points = rows.map((row) => toDataPoint(row, measure)).sort((first, second) => first.value - second.value);
    return {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            valueFormatter: (value) => formatValue(Number(value)),
        },
        grid: { left: "3%", right: "10%", bottom: "3%", containLabel: true },
        xAxis: { type: "value", axisLabel: { formatter: (value: number) => formatValue(value) } },
        yAxis: { type: "category", data: points.map((point) => point.name ?? "") },
        series: [
            {
                type: "bar",
                data: points,
                itemStyle: { color: (params) => barColorForIndex(params.dataIndex) },
                label: { show: true, position: "right", formatter: (params) => formatValue(Number(params.value)) },
            },
        ],
    };
}

/** Vertical bars in server order (e.g. pre-ordered bands such as percent planned). */
export function orderedBarOption(
    rows: ReportBreakdownRow[],
    measure: string,
    formatValue: ReportValueFormatter = formatReportNumber,
): EChartsOption {
    if (!rows.length) {
        return noDataOption();
    }
    const points = rows.map((row) => toDataPoint(row, measure));
    return {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            valueFormatter: (value) => formatValue(Number(value)),
        },
        grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
        xAxis: {
            type: "category",
            data: points.map((point) => point.name ?? ""),
            axisLabel: { interval: 0, rotate: 30 },
        },
        yAxis: { type: "value", axisLabel: { formatter: (value: number) => formatValue(value) } },
        series: [
            {
                type: "bar",
                data: points,
                itemStyle: { color: (params) => barColorForIndex(params.dataIndex) },
                label: { show: true, position: "top", formatter: (params) => formatValue(Number(params.value)) },
            },
        ],
    };
}

export function pieOption(
    rows: ReportBreakdownRow[],
    measure: string,
    formatValue: ReportValueFormatter = formatReportNumber,
): EChartsOption {
    const points = rows.map((row) => toDataPoint(row, measure)).filter((point) => point.value > 0);
    if (!points.length) {
        return noDataOption();
    }
    return {
        color: [...REPORT_PALETTE],
        tooltip: {
            trigger: "item",
            formatter: (params) => {
                const item = Array.isArray(params) ? params[0] : params;
                return `${item.name}: ${formatValue(Number(item.value))} (${item.percent}%)`;
            },
        },
        legend: { type: "scroll", bottom: 0 },
        series: [{ type: "pie", radius: ["35%", "65%"], data: points }],
    };
}

/**
 * One bar per series within each category. Categories keep first-seen server order; a category missing
 * from a series plots as 0 so series stay aligned.
 */
export function groupedBarOption(
    rows: ReportBreakdownRow[],
    series: ReportSeries[],
    measure: string,
    options: { horizontal?: boolean; formatValue?: ReportValueFormatter } = {},
): EChartsOption {
    if (!rows.length) {
        return noDataOption();
    }
    const formatValue = options.formatValue ?? formatReportNumber;
    const labelByRowKey = new Map<string, string>();
    for (const row of rows) {
        if (!labelByRowKey.has(row.key)) labelByRowKey.set(row.key, row.label);
    }
    const rowKeys = [...labelByRowKey.keys()];
    const effectiveSeries = series.length ? series : uniqueSeriesOf(rows);
    const rowBySeriesAndKey = new Map(rows.map((row) => [`${row.seriesKey}\u0000${row.key}`, row]));

    const chartSeries = effectiveSeries.map((reportSeries, seriesIndex) => ({
        name: reportSeries.label,
        type: "bar" as const,
        itemStyle: { color: REPORT_PALETTE[seriesIndex % REPORT_PALETTE.length] },
        data: rowKeys.map((rowKey): ReportDataPoint => {
            const row = rowBySeriesAndKey.get(`${reportSeries.key}\u0000${rowKey}`);
            return {
                value: row ? measureOf(row, measure) : 0,
                name: labelByRowKey.get(rowKey),
                rowKey,
                seriesKey: reportSeries.key,
            };
        }),
    }));

    const categoryAxis = { type: "category" as const, data: rowKeys.map((rowKey) => labelByRowKey.get(rowKey)) };
    const valueAxis = { type: "value" as const, axisLabel: { formatter: (value: number) => formatValue(value) } };
    return {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            valueFormatter: (value) => formatValue(Number(value)),
        },
        legend: { type: "scroll", bottom: 0 },
        grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
        xAxis: options.horizontal ? valueAxis : categoryAxis,
        yAxis: options.horizontal ? categoryAxis : valueAxis,
        series: chartSeries,
    };
}

function uniqueSeriesOf(rows: ReportBreakdownRow[]): ReportSeries[] {
    const seriesKeys = [...new Set(rows.map((row) => row.seriesKey))];
    return seriesKeys.map((seriesKey) => ({ key: seriesKey, label: seriesKey }));
}

export function buildReportChartOption(params: ReportChartOptionParams): EChartsOption {
    const rows = params.breakdown?.rows ?? [];
    const formatValue = params.formatValue ?? formatReportNumber;
    const chartType = resolveReportChartType(params.chartType, params.series.length);
    const singleSeriesKey = params.series.length === 1 ? params.series[0].key : undefined;
    const seriesRows = singleSeriesKey ? rows.filter((row) => row.seriesKey === singleSeriesKey) : rows;

    switch (chartType) {
        case "horizontalBar":
            return rankedBarOption(seriesRows, params.measure, formatValue);
        case "bar":
            return orderedBarOption(seriesRows, params.measure, formatValue);
        case "pie":
            return pieOption(seriesRows, params.measure, formatValue);
        case "groupedBar":
            return groupedBarOption(rows, params.series, params.measure, {
                horizontal: params.chartType === "horizontalBar",
                formatValue,
            });
        case "cards":
            return noDataOption("");
        default:
            return S25Assert.never(chartType, "Unknown report chart type");
    }
}

/** Reads the drill-down target carried on a clicked data point, or `null` for clicks off a segment. */
export function segmentFromChartClick(event: { data?: unknown } | null | undefined): ReportSegment | null {
    const data = event?.data as Partial<ReportDataPoint> | undefined;
    if (!data || typeof data !== "object" || typeof data.rowKey !== "string") {
        return null;
    }
    return { rowKey: data.rowKey, seriesKey: data.seriesKey };
}
